import * as React from "react";
import { Reorder, useDragControls } from "framer-motion";
import {
  Activity,
  LineChart,
  TrendingUp,
  BarChart3,
  Plus,
  X,
  GripVertical,
} from "lucide-react";
import {
  GlassCard,
  GlassCardContent,
  GlassCardHeader,
  GlassCardTitle,
  GlassCardDescription,
} from "@/components/ui/glass-card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PinnableRangeSlider } from "@/components/ui/pinnable-range-slider";
import { cn } from "@/utils/cn";
import { FALLBACK_TYPES } from "@/hooks/useFeatureSchema";
import type {
  FeatureParamSpec,
  FeatureTypeSpec,
  SlotDefault,
} from "@/hooks/useFeatureSchema";
import type { FeatureParamState } from "@/utils/featureParams";

// AIE-534: hard bounds on the variable-length feature list.
const MIN_FEATURES = 2;
const MAX_FEATURES = 15;

// Stable, collision-free id for a newly added slot. NOT the array index (reorders/removes would
// alias) and NOT Math.random() (non-deterministic dupes); the `feat-` prefix keeps the counter
// fallback from ever colliding with the positional String(i+1) padding ids minted elsewhere.
let _featureSeq = 0;
function mintFeatureId(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `feat-${Date.now().toString(36)}-${++_featureSeq}`;
}

export interface Feature {
  id: string;
  name: string;
  period: number;
  period2?: number;
  icon: React.ReactNode | null;
  color: string;
}

// Icon + gradient per feature type, kept for visual continuity only — it no longer drives
// arity (arity is contract-driven via featureParamsForType / the schema catalog).
const TYPE_VISUALS: Record<string, { icon: React.ReactNode; color: string }> = {
  RSI: {
    icon: <Activity className="w-4 h-4" />,
    color: "from-indigo-500 to-indigo-600",
  },
  WT: {
    icon: <LineChart className="w-4 h-4" />,
    color: "from-blue-500 to-blue-600",
  },
  CCI: {
    icon: <TrendingUp className="w-4 h-4" />,
    color: "from-cyan-500 to-cyan-600",
  },
  ADX: {
    icon: <BarChart3 className="w-4 h-4" />,
    color: "from-emerald-500 to-emerald-600",
  },
};

/**
 * Contract-driven arity: the ordered param specs for a feature type (RSI/CCI/WT = period +
 * second param; ADX = period only). Replaces the old INVERTED `requiresPeriod2` map.
 */
// eslint-disable-next-line react-refresh/only-export-components -- pure helper intentionally co-located with FeatureBuilder; consumed by NewStudyModern + tests
export function featureParamsForType(
  type: string,
  featureTypes: Record<string, { params: FeatureParamSpec[] }>,
): FeatureParamSpec[] {
  return featureTypes[type]?.params ?? [];
}

interface FeatureRowProps {
  feature: Feature;
  index: number;
  featureTypes: Record<string, FeatureTypeSpec>;
  rangeEnabled: boolean;
  featureParamState: FeatureParamState;
  handleTypeChange: (index: number, current: Feature, newType: string) => void;
  handleParamChange: (
    index: number,
    current: Feature,
    role: "period" | "multiplier",
    raw: string,
  ) => void;
  updateParam: (
    slotId: string,
    role: "period" | "multiplier",
    partial: Partial<{ value: number | [number, number]; pinned: boolean }>,
    fallbackScalar: number,
  ) => void;
  onRemove: (feature: Feature) => void;
  canRemove: boolean;
}

/**
 * One reorderable feature row. Each row owns its own `useDragControls()` instance — a hook, so it
 * MUST live in a per-row child component, not the parent's `.map` callback. `dragListener={false}`
 * disables whole-row dragging (the prior UX bug: dragging conflicted with the type <select> /
 * number inputs / range sliders); the GripVertical handle becomes the ONLY drag origin via
 * `onPointerDown -> controls.start(e)` (+ `touchAction: "none"` so touch-scroll doesn't hijack it).
 * State + handlers stay owned by FeatureBuilder and are threaded in as props.
 */
function FeatureRow({
  feature,
  index,
  featureTypes,
  rangeEnabled,
  featureParamState,
  handleTypeChange,
  handleParamChange,
  updateParam,
  onRemove,
  canRemove,
}: FeatureRowProps) {
  const controls = useDragControls();
  const typeNames = Object.keys(featureTypes);
  const params = featureParamsForType(feature.name, featureTypes);
  const visual = TYPE_VISUALS[feature.name] ?? {
    icon: feature.icon,
    color: feature.color,
  };
  return (
    <Reorder.Item
      value={feature}
      dragListener={false}
      dragControls={controls}
      data-testid={`feature-slot-row-${index}`}
      className={cn(
        "flex items-center gap-3 p-3 rounded-lg",
        "bg-dark-800/50 border border-dark-700",
      )}
    >
      <span
        data-testid={`feature-reorder-${feature.id}`}
        onPointerDown={(e) => controls.start(e)}
        style={{ touchAction: "none" }}
        className="self-start cursor-grab active:cursor-grabbing text-muted-foreground"
        aria-label="Drag to reorder"
      >
        <GripVertical className="w-4 h-4" />
      </span>
      <div
        className={cn(
          "self-start p-2 rounded-lg bg-gradient-to-r",
          visual.color,
        )}
      >
        {visual.icon}
      </div>
      <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <div className={cn(rangeEnabled && "h-7 flex items-center")}>
            <Label
              htmlFor={`feature-${index}-type`}
              className={cn(rangeEnabled && "leading-5")}
            >
              Indicator
            </Label>
          </div>
          <select
            id={`feature-${index}-type`}
            data-testid={`feature-type-select-${index}`}
            value={feature.name}
            onChange={(e) => handleTypeChange(index, feature, e.target.value)}
            className={cn(
              "w-full rounded-md border border-dark-700 bg-dark-800 px-3 py-2 text-sm",
              rangeEnabled ? "mt-4" : "mt-1",
            )}
          >
            {typeNames.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        {params.map((param) => {
          const fallbackScalar =
            param.wire_role === "period"
              ? feature.period
              : (feature.period2 ?? param.default);
          if (rangeEnabled) {
            const st = featureParamState[feature.id]?.[param.wire_role];
            const value = st?.value ?? fallbackScalar;
            const pinned = st?.pinned ?? true;
            return (
              <div
                key={param.wire_role}
                data-testid={`feature-range-${index}-${param.wire_role}`}
              >
                <PinnableRangeSlider
                  label={param.label}
                  min={param.min}
                  max={param.max}
                  step={param.step ?? 1}
                  value={value}
                  isPinned={pinned}
                  onValueChange={(v) =>
                    updateParam(
                      feature.id,
                      param.wire_role,
                      { value: v },
                      fallbackScalar,
                    )
                  }
                  onPinnedChange={(p) =>
                    updateParam(
                      feature.id,
                      param.wire_role,
                      { pinned: p },
                      fallbackScalar,
                    )
                  }
                />
              </div>
            );
          }
          const inputId = `feature-${index}-${param.wire_role}`;
          return (
            <div key={param.wire_role}>
              <Label htmlFor={inputId}>{param.label}</Label>
              <Input
                id={inputId}
                type="number"
                min={param.min}
                max={param.max}
                step={param.step ?? 1}
                value={fallbackScalar}
                onChange={(e) =>
                  handleParamChange(
                    index,
                    feature,
                    param.wire_role,
                    e.target.value,
                  )
                }
                className="mt-1"
              />
            </div>
          );
        })}
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        data-testid={`feature-remove-${feature.id}`}
        disabled={!canRemove}
        onClick={() => onRemove(feature)}
        aria-label={`Remove ${feature.name} slot`}
      >
        <X className="w-4 h-4" />
      </Button>
    </Reorder.Item>
  );
}

interface FeatureBuilderProps {
  features: Feature[];
  onChange: (features: Feature[]) => void;
  // Catalog from useFeatureSchema (fallback to the built-in catalog if absent). slotDefaults is
  // accepted for forward-compat (range bounds); normalization lives in NewStudyModern.
  featureTypes?: Record<string, FeatureTypeSpec>;
  slotDefaults?: SlotDefault[];
  // Phase 6: flag-gated range controls. When false (default) only the fixed Phase-4 inputs render;
  // the type/period edit + routing path stays unconditional regardless of this flag.
  rangeEnabled?: boolean;
  featureParamState?: FeatureParamState;
  onParamStateChange?: (
    updater: (prev: FeatureParamState) => FeatureParamState,
  ) => void;
}

export function FeatureBuilder({
  features,
  onChange,
  featureTypes = FALLBACK_TYPES,
  rangeEnabled = false,
  featureParamState = {},
  onParamStateChange,
}: FeatureBuilderProps) {
  const typeNames = Object.keys(featureTypes);

  const updateSlot = (index: number, next: Feature) => {
    onChange(features.map((f, i) => (i === index ? next : f)));
  };

  // Functional update so PinnableRangeSlider's synchronous pin-toggle (which fires BOTH
  // onPinnedChange and onValueChange) never clobbers itself via a stale closure.
  const updateParam = (
    slotId: string,
    role: "period" | "multiplier",
    partial: Partial<{ value: number | [number, number]; pinned: boolean }>,
    fallbackScalar: number,
  ) => {
    onParamStateChange?.((prev) => {
      const slot = { ...(prev[slotId] ?? {}) };
      const existing = slot[role] ?? { value: fallbackScalar, pinned: true };
      slot[role] = { ...existing, ...partial };
      return { ...prev, [slotId]: slot };
    });
  };

  const handleTypeChange = (
    index: number,
    current: Feature,
    newType: string,
  ) => {
    const params = featureParamsForType(newType, featureTypes);
    const periodSpec = params.find((p) => p.wire_role === "period");
    const multSpec = params.find((p) => p.wire_role === "multiplier");
    const visual = TYPE_VISUALS[newType] ?? { icon: null, color: "" };
    const next: Feature = {
      ...current,
      name: newType,
      period: periodSpec?.default ?? current.period,
      icon: visual.icon,
      color: visual.color,
    };
    // Re-seed the second param from the new type's catalog default, or drop it (ADX has none).
    if (multSpec) {
      next.period2 = multSpec.default;
    } else {
      delete next.period2;
    }
    updateSlot(index, next);
    // Reset this slot's range UI state so stale ranges/params can't leak across a type change;
    // the new type's params fall back to pinned-at-default.
    onParamStateChange?.((prev) => {
      if (!prev[current.id]) return prev;
      const reset = { ...prev };
      delete reset[current.id];
      return reset;
    });
  };

  const handleParamChange = (
    index: number,
    current: Feature,
    role: "period" | "multiplier",
    raw: string,
  ) => {
    const parsed = parseInt(raw);
    const next: Feature = { ...current };
    if (role === "period") {
      next.period = Number.isNaN(parsed) ? current.period : parsed;
    } else {
      next.period2 = Number.isNaN(parsed) ? current.period2 : parsed;
    }
    updateSlot(index, next);
  };

  // Append a new slot seeded from a default type's catalog params (contract-driven arity — ADX gets
  // no period2). Disabled past MAX_FEATURES at the call site.
  const handleAddFeature = () => {
    if (features.length >= MAX_FEATURES) return;
    const defaultType = typeNames[0] ?? "RSI";
    const params = featureParamsForType(defaultType, featureTypes);
    const periodSpec = params.find((p) => p.wire_role === "period");
    const multSpec = params.find((p) => p.wire_role === "multiplier");
    const visual = TYPE_VISUALS[defaultType] ?? { icon: null, color: "" };
    const next: Feature = {
      id: mintFeatureId(),
      name: defaultType,
      period: periodSpec?.default ?? 14,
      icon: visual.icon,
      color: visual.color,
    };
    if (multSpec) {
      next.period2 = multSpec.default;
    }
    onChange([...features, next]);
  };

  // Remove a slot by id (never by index — ids are the stable key) and prune its range UI state so a
  // removed slot can never leak a stale spec into the next submit. Disabled at MIN_FEATURES.
  const handleRemoveFeature = (feature: Feature) => {
    if (features.length <= MIN_FEATURES) return;
    onChange(features.filter((f) => f.id !== feature.id));
    onParamStateChange?.((prev) => {
      if (!prev[feature.id]) return prev;
      const next = { ...prev };
      delete next[feature.id];
      return next;
    });
  };

  return (
    <GlassCard>
      <GlassCardHeader>
        <GlassCardTitle>Technical Indicators</GlassCardTitle>
        <GlassCardDescription>
          Configure the Lorentzian feature slots — drag to reorder, add or
          remove slots (2–15)
        </GlassCardDescription>
      </GlassCardHeader>
      <GlassCardContent>
        <Reorder.Group
          axis="y"
          values={features}
          onReorder={onChange}
          className="space-y-2 list-none"
        >
          {features.map((feature, index) => (
            <FeatureRow
              key={feature.id}
              feature={feature}
              index={index}
              featureTypes={featureTypes}
              rangeEnabled={rangeEnabled}
              featureParamState={featureParamState}
              handleTypeChange={handleTypeChange}
              handleParamChange={handleParamChange}
              updateParam={updateParam}
              onRemove={handleRemoveFeature}
              canRemove={features.length > MIN_FEATURES}
            />
          ))}
        </Reorder.Group>
        <Button
          type="button"
          variant="outline"
          className="w-full mt-4"
          data-testid="feature-add-button"
          disabled={features.length >= MAX_FEATURES}
          onClick={handleAddFeature}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add feature slot
        </Button>
      </GlassCardContent>
    </GlassCard>
  );
}
